/*
  wishlist-page.js
  Renders the dedicated wishlist page from localStorage.

  Depends on wishlist.js, which exposes:
    window.WishlistStorage  { getAll, has, add, remove, count }
    window.WishlistUI       { updateBadge, updateAllButtons }

  Items are stored client-side only, so everything here is rendered in the
  browser. Markup is built once and event handling is delegated from the root,
  which keeps re-renders cheap on long lists.
*/
(function () {
  'use strict';

  var CART_ADD = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root + 'cart/add.js'
    : '/cart/add.js';

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function WishlistPageView(rootEl) {
    var sectionId = rootEl.id.replace('wlp-', '');
    var i18nNode = document.querySelector('[data-wlp-i18n-' + sectionId + ']');
    var t = {};
    try { t = JSON.parse(i18nNode.textContent); } catch (e) { t = {}; }

    var listRoot = rootEl.querySelector('[data-wlp-root]');
    var skeleton = rootEl.querySelector('[data-wlp-skeleton]');
    var countEl = rootEl.querySelector('[data-wlp-count]');
    var bulkBar = rootEl.querySelector('[data-wlp-bulkbar]');
    var addAllBtn = rootEl.querySelector('[data-wlp-addall]');
    var addAllText = rootEl.querySelector('[data-wlp-addall-text]');
    var clearBtn = rootEl.querySelector('[data-wlp-clear]');
    var continueUrl = rootEl.dataset.continueUrl || '/collections/all';

    function store() {
      return window.WishlistStorage;
    }

    function items() {
      return store() ? store().getAll() : [];
    }

    function cardHTML(p) {
      var img = p.image || '';
      var variant = p.variantTitle && p.variantTitle !== 'Default Title'
        ? '<p class="wlp-card__variant">' + esc(p.variantTitle) + '</p>'
        : '';
      var vendor = p.vendor ? '<p class="wlp-card__vendor">' + esc(p.vendor) + '</p>' : '';
      var url = '/products/' + esc(p.handle);
      var hasVariant = p.variantId !== undefined && p.variantId !== null && p.variantId !== '';

      return '' +
        '<article class="wlp-card" data-wlp-item="' + esc(String(p.id)) + '">' +
          '<div class="wlp-card__media">' +
            '<a href="' + url + '" class="wlp-card__imglink" tabindex="-1" aria-hidden="true">' +
              (img
                ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="300" height="300">'
                : '<span class="wlp-card__noimg"></span>') +
            '</a>' +
            '<button type="button" class="wlp-card__remove" data-wlp-remove="' + esc(String(p.id)) + '" aria-label="' + esc(t.remove || 'Remove') + '">' +
              '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
                '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
              '</svg>' +
            '</button>' +
          '</div>' +
          '<div class="wlp-card__body">' +
            vendor +
            '<a href="' + url + '" class="wlp-card__title">' + esc(p.title) + '</a>' +
            variant +
            '<p class="wlp-card__price" data-wl-price data-handle="' + esc(p.handle) + '" data-variant="' + esc(String(p.variantId || '')) + '">' + esc(p.price) + '</p>' +
            '<button type="button" class="wlp-btn wlp-btn--solid wlp-card__atc"' +
              (hasVariant ? ' data-wlp-atc="' + esc(String(p.variantId)) + '"' : ' disabled') + '>' +
              esc(hasVariant ? (t.add_to_cart || 'Add to cart') : (t.sold_out || 'Unavailable')) +
            '</button>' +
          '</div>' +
        '</article>';
    }

    function emptyHTML() {
      return '' +
        '<div class="wlp-empty">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
          '<h2>' + esc(t.empty_title || 'Your wishlist is empty') + '</h2>' +
          '<p>' + esc(t.empty_text || '') + '</p>' +
          '<a href="' + esc(continueUrl) + '" class="wlp-btn wlp-btn--solid">' + esc(t.continue || 'Continue shopping') + '</a>' +
        '</div>';
    }

    function setCount(n) {
      if (!countEl) return;
      if (!n) { countEl.hidden = true; return; }
      var tpl = n === 1 ? (t.item_one || '1 item saved') : (t.item_other || '[count] items saved');
      countEl.textContent = tpl.replace('[count]', n);
      countEl.hidden = false;
    }

    function render() {
      var list = items();

      if (skeleton) skeleton.hidden = true;
      listRoot.hidden = false;

      setCount(list.length);
      /* Bulk actions only make sense across multiple items — one saved
         product already has its own Add to cart and Remove on the card. */
      if (bulkBar) bulkBar.hidden = list.length < 2;

      if (!list.length) {
        listRoot.innerHTML = emptyHTML();
        return;
      }

      var html = '<div class="wlp-grid">';
      for (var i = 0; i < list.length; i++) html += cardHTML(list[i]);
      html += '</div>';
      listRoot.innerHTML = html;
    }

    /* cart/add.js answers 422 with a JSON body whose `description` says what
       went wrong. The old code rejected on the raw Response and swallowed it,
       so a failed add looked identical to nothing happening. */
    function postAdd(lines) {
      return fetch(CART_ADD, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: lines })
      }).then(function (r) {
        return r.text().then(function (raw) {
          var data = {};
          try { data = JSON.parse(raw); } catch (e) { data = {}; }
          if (r.ok) return data;
          var err = new Error(data.description || data.message || ('cart/add ' + r.status));
          err.status = r.status;
          err.data = data;
          throw err;
        });
      });
    }

    function line(p) {
      return { id: Number(p.variantId), quantity: 1 };
    }

    function refreshCart() {
      if (window.WishlistCart && window.WishlistCart.refresh) {
        window.WishlistCart.refresh();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    }

    var errTimer;
    function showError(msg) {
      var box = rootEl.querySelector('[data-wlp-error]');
      if (!box) {
        box = document.createElement('p');
        box.className = 'wlp-error';
        box.setAttribute('data-wlp-error', '');
        box.setAttribute('role', 'alert');
        listRoot.parentNode.insertBefore(box, listRoot);
      }
      box.textContent = msg;
      box.hidden = false;
      clearTimeout(errTimer);
      errTimer = setTimeout(function () { box.hidden = true; }, 6000);
    }

    /* Drops ids from storage in one pass, then re-renders once. Used after a
       successful add — the item now lives in the cart, so keeping it saved
       would leave the shopper removing it by hand. */
    function dropItems(ids) {
      if (!ids.length) return;
      ids.forEach(function (id) {
        var card = listRoot.querySelector('[data-wlp-item="' + id + '"]');
        if (card) card.classList.add('is-removing');
      });
      setTimeout(function () {
        if (store()) ids.forEach(function (id) { store().remove(id); });
        if (window.WishlistUI) {
          window.WishlistUI.updateBadge();
          window.WishlistUI.updateAllButtons();
        }
        render();
      }, 240);
    }

    function addToCart(variantId, btn, productId) {
      var original = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = t.adding || 'Adding…'; }

      return postAdd([{ id: Number(variantId), quantity: 1 }])
        .then(function () {
          if (btn) {
            btn.textContent = t.added || 'Added';
            btn.classList.add('is-added');
          }
          refreshCart();
          document.dispatchEvent(new CustomEvent('wlp:cart-updated'));
          /* Let the Added state read before the card leaves. */
          if (productId) {
            setTimeout(function () { dropItems([productId]); }, 700);
          } else if (btn) {
            setTimeout(function () {
              btn.textContent = original;
              btn.disabled = false;
              btn.classList.remove('is-added');
            }, 1600);
          }
          return true;
        })
        .catch(function (err) {
          console.error('[wishlist] add to cart failed', variantId, err.status, err.data || err);
          showError(err.message);
          if (btn) { btn.textContent = original; btn.disabled = false; }
          return false;
        });
    }

    function removeItem(id) {
      dropItems([id]);
    }

    /* Delegated events — survives re-renders */
    listRoot.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-wlp-remove]');
      if (rm) { removeItem(rm.getAttribute('data-wlp-remove')); return; }

      var atc = e.target.closest('[data-wlp-atc]');
      if (atc) {
        var card = atc.closest('[data-wlp-item]');
        addToCart(
          atc.getAttribute('data-wlp-atc'),
          atc,
          card ? card.getAttribute('data-wlp-item') : null
        );
      }
    });

    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        var list = items().filter(function (p) { return p.variantId; });
        if (!list.length) return;
        var label = addAllText ? addAllText.textContent : '';
        addAllBtn.disabled = true;
        if (addAllText) addAllText.textContent = t.adding || 'Adding…';

        /* Only the lines that actually landed in the cart get unsaved, so a
           failed item stays on the wishlist for the shopper to retry. */
        var added = [];

        /* One unavailable line 422s the whole batch and nothing gets added, so
           fall back to adding them one at a time and report only the failures. */
        postAdd(list.map(line))
          .then(function () {
            list.forEach(function (p) { added.push(p.id); });
          })
          .catch(function (batchErr) {
            var failed = [];
            return list.reduce(function (chain, p) {
              return chain.then(function () {
                return postAdd([line(p)])
                  .then(function () { added.push(p.id); })
                  .catch(function (e) { failed.push(p.title + ' — ' + e.message); });
              });
            }, Promise.resolve()).then(function () {
              if (!added.length) throw batchErr;
              if (failed.length) showError(failed.join(' | '));
            });
          })
          .then(function () {
            if (addAllText) addAllText.textContent = t.added || 'Added';
            refreshCart();
            document.dispatchEvent(new CustomEvent('wlp:cart-updated'));
            setTimeout(function () { dropItems(added); }, 700);
          })
          .catch(function (err) {
            console.error('[wishlist] add all to cart failed', err.status, err.data || err);
            showError(err.message);
            if (addAllText) addAllText.textContent = label;
          })
          .finally(function () {
            setTimeout(function () {
              addAllBtn.disabled = false;
              if (addAllText) addAllText.textContent = label;
            }, 1600);
          });
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (!window.confirm(t.confirm_clear || 'Remove all items?')) return;
        items().forEach(function (p) { if (store()) store().remove(p.id); });
        if (window.WishlistUI) {
          window.WishlistUI.updateBadge();
          window.WishlistUI.updateAllButtons();
        }
        render();
      });
    }

    /* Keep in sync with hearts elsewhere / other tabs */
    window.addEventListener('storage', render);
    document.addEventListener('wishlist:changed', render);

    render();
  }

  ready(function () {
    document.querySelectorAll('[data-wishlist-page]').forEach(function (el) {
      if (el.dataset.wlpBound) return;
      el.dataset.wlpBound = '1';
      // wishlist.js is deferred too; wait a tick if storage is not ready yet
      if (window.WishlistStorage) {
        WishlistPageView(el);
      } else {
        var tries = 0;
        var iv = setInterval(function () {
          if (window.WishlistStorage || ++tries > 40) {
            clearInterval(iv);
            WishlistPageView(el);
          }
        }, 50);
      }
    });
  });
})();
